import React from 'react';
import { Chip } from '@mui/material';
import {
    ArrayField,
    CreateButton,
    Datagrid,
    DateField,
    EditButton,
    ExportButton,
    FilterButton,
    List,
    ShowButton,
    TextField,
    TextInput,
    TopToolbar,
    useListContext,
    useNotify,
} from 'react-admin';
import YesNoField from '../../material/yesNoField.component';
import { DATE_TIME_OPTIONS } from '../../app/app.component';
import PeriodInput from "../../app/periodInput.component";
import DefaultPlaceholderField from '../../material/defPlaceholderField.component';
import { orgsFields } from '../orgs.model';
import OrganizationLinkField from './fields/organizationLink.component';
import { OrganizationSidebarFilters } from './OrganizationSidebarFilters';
import {DEFAULT_LIST_PERPAGE} from "utils/constants";
import { organizationExporter } from "@/organizations/utils/organizationExport";
import EnrollmentMethodField from './fields/enrollmentMethod.component';
import InsurancePlanField from './fields/healthieInsurance.component';

const filters = [
    <TextInput source={orgsFields.orgName} label="Name" alwaysOn />,
    <PeriodInput
        source="createdBetween"
        fromLabel="Created At From"
        toLabel="Created At To"
        label="Created At"
    />,
    <PeriodInput
        source="updatedBetween"
        fromLabel="Updated At From"
        toLabel="Updated At To"
        label="Updated At"
    />
];

const USStatesField = () => {
    const maxItems = 5;
    const { data } = useListContext();
    const total = data.length;
    const items = data.reduce((prev, curr, index) => {
        if (index < maxItems) {
            return [...prev, curr];
        }
        if (index === maxItems) {
            return [...prev, `+${total - maxItems} more`];
        }
        return prev;
    } ,[])
    
    return (
        <>
            {items.map((item, index) => (
                <Chip
                    key={item}
                    label={item}
                    sx={{
                        m: 0.25,
                        opacity: index === maxItems ? 0.7 : 1
                    }}
                />
            ))}
        </>
    )
}

const ListActions = () => {
    const { total } = useListContext();
    const notify = useNotify();

    return (
        <TopToolbar>
            <FilterButton />
            <CreateButton />
            <ExportButton
                disabled={total === 0}
                exporter={(organizations) => organizationExporter(organizations, notify)}
            />
        </TopToolbar>
    );
};

export const OrgsList = () => (
    <List
        exporter={organizationExporter}
        filters={filters}
        actions={<ListActions />}
        aside={<OrganizationSidebarFilters />}
        sort={{ field: 'id', order: 'DESC' }}
        perPage={DEFAULT_LIST_PERPAGE}
        sx={{
            '& .single-line-ellipsis': {
                maxWidth: '5em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            }
        }}
    >
        <Datagrid bulkActionButtons={false}>
            <OrganizationLinkField label="Id" source={orgsFields.id} />
            <DefaultPlaceholderField 
                source={orgsFields.clientGroupId} 
                wrapField={<TextField />} 
                label="Healthie Client Group ID" 
            />
            <DefaultPlaceholderField 
                source={orgsFields.orgName} 
                wrapField={<TextField />} 
                label="Name" 
            />
           
            <DefaultPlaceholderField 
                source={orgsFields.orgNursePhone} 
                wrapField={<TextField />} 
                label="Nurse Phone" 
            />
            <DefaultPlaceholderField 
                source={orgsFields.payerId} 
                wrapField={<TextField />} 
                label="Payer ID" 
            />
             <DefaultPlaceholderField 
                source={orgsFields.orgCreatedAt} 
                wrapField={<DateField />} 
                options={DATE_TIME_OPTIONS} 
                label="Created At" 
            />
            <DefaultPlaceholderField 
                source={orgsFields.orgUpdatedAt} 
                wrapField={<DateField />} 
                options={DATE_TIME_OPTIONS} 
                label="Updated At" 
            />
            <YesNoField
                source={orgsFields.isActive}
                label="Active"
            />
            <DefaultPlaceholderField
                source={orgsFields.healthieInsuranceId}
                wrapField={<InsurancePlanField />}
                label="Healthie Insurance"
            />
            <DefaultPlaceholderField 
                source={orgsFields.enrollmentMethod} 
                wrapField={<EnrollmentMethodField />} 
                label="Enrollment Method"
                emptyValue=""
            />
            <ArrayField source={orgsFields.usStates} label="US States">
                <USStatesField />
            </ArrayField>
            <ShowButton />
            <EditButton />
        </Datagrid>
    </List>
);
